import { filterNulls } from "replo-utils/lib/array";
import { z } from "zod";

import {
  designDirectionRequestSchema,
  designDirectionSetSchema,
} from "../../designDirection";

export const mediaMcpReferenceAssetSchema = z
  .object({
    r2Uri: z.string().optional(),
    url: z.url().optional(),
    base64Data: z.string().optional(),
    mimeType: z.string().optional(),
  })
  .refine((value) => {
    return Boolean(value.r2Uri ?? value.url ?? value.base64Data);
  }, "Each reference asset must include one source field");

export const mediaMcpImageModelSchema = z
  .enum([
    "gemini-3.1-flash-image",
    "gemini-3.1-flash-image-preview",
    "gpt-image-2.5-sunburst",
    "gpt-image-2.5-flare",
    "gemini-3-pro-image",
    "flux.2-pro",
    "seedream-5-0-lite",
  ])
  .describe("MediaMcpImageModel");

// OpenRouter exposes quality on GPT Image; other models use their own defaults.
export const mediaMcpImageQualitySchema = z
  .enum(["low", "medium", "high"])
  .describe("MediaMcpImageQuality");

const mediaMcpImageSizeSchema = z
  .string()
  .regex(/^\d+x\d+$/, "size must be WIDTHxHEIGHT, e.g. 1280x2560")
  .describe(
    "Pixels as WIDTHxHEIGHT, e.g. 1024x1536. Not an aspect ratio like 16:9.",
  );

// Where a generated image lands. "chatFile" (default) is a private, signed,
// project-scoped file hidden from the Assets and Files apps — the target for
// review-then-choose creative work, so generations don't auto-clutter Assets.
// "asset" is a durable public asset on the CDN with a stable URL, required for
// page-ready imagery that gets referenced in code or a published page.
export const mediaMcpImagePersistTargetSchema = z
  .enum(["asset", "chatFile"])
  .describe("MediaMcpImagePersistTarget");

export const mediaMcpGenerateImageRequestSchema = z
  .object({
    prompt: z.string().min(1),
    references: z.array(mediaMcpReferenceAssetSchema).optional(),
    assetName: z.string().min(1).optional(),
    outputFileExtension: z.string().optional(),
    model: mediaMcpImageModelSchema.optional(),
    size: mediaMcpImageSizeSchema.optional(),
    quality: mediaMcpImageQualitySchema.optional(),
    persistAs: mediaMcpImagePersistTargetSchema.optional(),
  })
  .describe("MediaMcpGenerateImageRequest");

export const mediaMcpGenerateImageInputSchema = z
  .object({
    prompt: z.string().min(1).optional(),
    references: z.array(mediaMcpReferenceAssetSchema).optional(),
    assetName: z.string().min(1).optional(),
    outputFileExtension: z.string().optional(),
    model: mediaMcpImageModelSchema.optional(),
    size: mediaMcpImageSizeSchema.optional(),
    quality: mediaMcpImageQualitySchema.optional(),
    persistAs: mediaMcpImagePersistTargetSchema.optional(),
    requests: z
      .array(mediaMcpGenerateImageRequestSchema)
      .min(1)
      .max(10)
      .optional(),
  })
  .superRefine((value, context) => {
    const hasSingleRequest = Boolean(value.prompt);
    const hasBatchRequests = Boolean(value.requests);

    if (!hasSingleRequest && !hasBatchRequests) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Provide either prompt or requests",
      });
    }

    if (hasSingleRequest && hasBatchRequests) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Provide prompt for single request or requests for batch",
      });
    }
  });

export const mediaMcpRemoveImageBackgroundInputSchema = z
  .object({
    image: mediaMcpReferenceAssetSchema,
    assetName: z.string().min(1).optional(),
    persistAs: mediaMcpImagePersistTargetSchema.optional(),
  })
  .describe("MediaMcpRemoveImageBackgroundInput");

// Bounds mirror Seedance 2.5 on OpenRouter so a bad request fails at the
// agent's client instead of as a vendor 400 after the credit gate.
export const mediaMcpGenerateVideoInputSchema = z.object({
  prompt: z.string().min(1),
  references: z.array(mediaMcpReferenceAssetSchema).optional(),
  assetName: z.string().min(1).optional(),
  aspectRatio: z.enum(["16:9", "9:16", "1:1", "4:3", "3:4", "21:9"]).optional(),
  durationSeconds: z.number().int().min(4).max(30).optional(),
});

const mediaMcpJobIdSchema = z.string().min(1);

// Agents most often pass `{ jobId }` for a single job, so both spellings and
// both shapes are accepted and normalized server-side.
export const mediaMcpGetGenerationStatusInputSchema = z
  .object({
    jobIds: z
      .union([z.array(mediaMcpJobIdSchema).min(1), mediaMcpJobIdSchema])
      .optional()
      .describe(
        'Job ids to check, e.g. ["<jobId>", "<jobId>"]. Any number; a single id string also works.',
      ),
    jobId: mediaMcpJobIdSchema
      .optional()
      .describe("One job id; same as jobIds: [jobId]."),
  })
  .refine((value) => Boolean(value.jobIds ?? value.jobId), {
    message: 'Pass jobIds: ["<jobId>", ...] (or jobId for a single job).',
  });

export function getRequestedGenerationJobIds(
  input: z.infer<typeof mediaMcpGetGenerationStatusInputSchema>,
): string[] {
  const jobIds = Array.isArray(input.jobIds) ? input.jobIds : [input.jobIds];
  return [...new Set(filterNulls([...jobIds, input.jobId]))];
}

export const mediaMcpGeneratedAssetOutputSchema = z
  .object({
    mimeType: z.string(),
    model: z.string(),
    sizeBytes: z.number(),
    url: z.string(),
  })
  .describe("MediaMcpGeneratedAssetOutput");

export const mediaMcpImageAssetOutputSchema = mediaMcpGeneratedAssetOutputSchema
  .omit({ model: true })
  .describe("MediaMcpImageAssetOutput");

// Billing as the agent sees it: credits, never vendor dollars. "pending" means
// the vendor charged but the credit ledger has not recorded it yet;
// "not_charged" is a terminal job with no vendor cost on record.
export const mediaMcpBillingStatusSchema = z
  .enum(["charged", "pending", "not_charged"])
  .describe("MediaMcpBillingStatus");

export const mediaMcpRemoveImageBackgroundOutputSchema = z
  .object({
    asset: mediaMcpImageAssetOutputSchema,
    billingStatus: mediaMcpBillingStatusSchema,
    creditsCharged: z.number().int().nonnegative().optional(),
    success: z.literal(true),
  })
  .describe("MediaMcpRemoveImageBackgroundOutput");

export const mediaMcpGenerationJobKindSchema = z.enum(["image", "video"]);

export const mediaMcpGenerationJobOutputSchema = z
  .object({
    asset: mediaMcpGeneratedAssetOutputSchema.optional(),
    assetName: z.string().optional(),
    billingStatus: mediaMcpBillingStatusSchema.optional(),
    createdAt: z.string(),
    creditsCharged: z.number().int().nonnegative().optional(),
    error: z.string().optional(),
    estimatedDurationSeconds: z.number().optional(),
    estimatedSecondsRemaining: z.number().optional(),
    jobId: z.string(),
    kind: mediaMcpGenerationJobKindSchema,
    model: z.string(),
    progressPercent: z.number().optional(),
    status: z.enum(["queued", "in_progress", "completed", "failed"]),
    statusDetail: z.string().optional(),
    updatedAt: z.string(),
  })
  .describe("MediaMcpGenerationJobOutput");

export const mediaMcpGenerateJobOutputSchema = mediaMcpGenerationJobOutputSchema
  .partial()
  .extend({
    jobIds: z.array(z.string()).optional(),
    nextAction: z.string(),
    results: z.array(mediaMcpGenerationJobOutputSchema).optional(),
    success: z.boolean(),
    total: z.number().optional(),
  });

// A jobId with no stored record can only be named by id and terminal status, so
// it gets its own arm rather than loosening the one a stored record must satisfy.
const mediaMcpMissingGenerationJobOutputSchema = z.object({
  error: z.string(),
  jobId: z.string(),
  status: z.literal("failed"),
  statusDetail: z.literal("not_found"),
  success: z.literal(false),
});

export const mediaMcpGetGenerationStatusOutputSchema = z.object({
  failureCount: z.number(),
  results: z.array(
    z.union([
      mediaMcpGenerationJobOutputSchema.extend({
        success: z.boolean(),
      }),
      mediaMcpMissingGenerationJobOutputSchema,
    ]),
  ),
  success: z.boolean(),
  successCount: z.number(),
  total: z.number(),
});

export const mediaMcpDesignDirectionSetOutputSchema = designDirectionSetSchema
  .extend({
    estimatedDurationSeconds: z.number().optional(),
    nextAction: z.string(),
    success: z.boolean(),
  })
  .describe("MediaMcpDesignDirectionSetOutput");

export const knownMediaMcpToolMetadataRegistrySchema = z
  .object({
    generate_design_directions: z.object({
      iconKey: z.literal("mediaMcp"),
      input: designDirectionRequestSchema,
      output: mediaMcpDesignDirectionSetOutputSchema,
    }),
    generate_image: z.object({
      iconKey: z.literal("mediaMcp"),
      input: mediaMcpGenerateImageInputSchema,
      output: mediaMcpGenerateJobOutputSchema,
    }),
    generate_video: z.object({
      iconKey: z.literal("mediaMcp"),
      input: mediaMcpGenerateVideoInputSchema,
      output: mediaMcpGenerateJobOutputSchema,
    }),
    remove_image_background: z.object({
      iconKey: z.literal("mediaMcp"),
      input: mediaMcpRemoveImageBackgroundInputSchema,
      output: mediaMcpRemoveImageBackgroundOutputSchema,
    }),
    get_generation_status: z.object({
      iconKey: z.literal("mediaMcp"),
      input: mediaMcpGetGenerationStatusInputSchema,
      output: mediaMcpGetGenerationStatusOutputSchema,
    }),
  })
  .describe("KnownMediaMcpToolMetadataRegistry");
